import React, { useState, useEffect, useRef } from "react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { Member, MemberPermission } from "./types/memberTypes"
import { updatePermission } from "./types/permissionTypes"

interface MemberPermissionsCellProps {
  member: Member
  handlePermissionChange: (
    memberId: string,
    permissionId: string,
    checked: boolean
  ) => void
}

const MemberPermissionsCell: React.FC<MemberPermissionsCellProps> = ({
  member,
  handlePermissionChange,
}) => {
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Use member role directly - it's the source of truth from the portfolio document
  const isOwnerRole = member.role === "Primary Owner" || member.role === "Owner"

  // Ensure permissions array exists
  let permissions = member.permissions || []

  // If owner role, force all permissions to be checked, but exclude viewOnly
  if (isOwnerRole) {
    permissions = permissions
      .filter((option) => option.id !== "viewOnly") // Exclude viewOnly for owners
      .map((option) => ({
        ...option,
        checked: true,
      }))
  }

  // Count checked permissions
  const checkedCount = permissions.filter((p) => p.checked).length

  // Group permissions by type
  const portfolioPermissions = permissions.filter((p) =>
    ["portfolioUsers", "portfolioSettings", "viewOnly"].includes(p.id)
  )

  const businessPermissions = permissions.filter((p) =>
    [
      "campaigns",
      "budget",
      "billing",
      "leads",
      "businessUsers",
      "businessCampaigns",
      "businessLeads",
      "businessSettings",
    ].includes(p.id)
  )

  // Function to handle permission change with View Only logic
  const handlePermissionChangeWithViewOnly = (
    memberId: string,
    permissionId: string,
    checked: boolean
  ) => {
    const updatedPermissions = updatePermission(
      permissions,
      permissionId,
      checked
    )

    // Update each permission individually
    updatedPermissions.forEach((permission) => {
      if (
        permission.checked !==
        permissions.find((p) => p.id === permission.id)?.checked
      ) {
        handlePermissionChange(memberId, permission.id, permission.checked)
      }
    })

    // Keep dropdown open after permission change
    setOpen(true)
  }

  return (
    <div ref={dropdownRef}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            {isOwnerRole ? "All Permissions" : "Permissions"}
            <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs">
              {isOwnerRole ? permissions.length : checkedCount}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-56 bg-white"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {/* If owner role, all permissions are granted automatically */}
          {isOwnerRole ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              All permissions granted to {member.role}
            </div>
          ) : (
            <>
              {/* Portfolio Permissions Group */}
              <DropdownMenuLabel>Portfolio Permissions</DropdownMenuLabel>
              {portfolioPermissions.map((permission) => (
                <DropdownMenuCheckboxItem
                  key={permission.id}
                  checked={permission.checked}
                  disabled={
                    permission.id === "viewOnly" &&
                    permissions.some((p) => p.id !== "viewOnly" && p.checked)
                  }
                  onSelect={(e) => {
                    e.preventDefault()
                    e.stopPropagation() // Prevent event bubbling that might close dropdown
                  }}
                  onCheckedChange={(checked) => {
                    handlePermissionChangeWithViewOnly(
                      member.id,
                      permission.id,
                      !!checked
                    )
                  }}
                >
                  {permission.name}
                </DropdownMenuCheckboxItem>
              ))}

              <DropdownMenuSeparator />

              {/* Business Permissions Group */}
              <DropdownMenuLabel>Business Permissions</DropdownMenuLabel>
              {businessPermissions.map((permission) => (
                <DropdownMenuCheckboxItem
                  key={permission.id}
                  checked={permission.checked}
                  onSelect={(e) => {
                    e.preventDefault()
                    e.stopPropagation() // Prevent event bubbling that might close dropdown
                  }}
                  onCheckedChange={(checked) => {
                    handlePermissionChangeWithViewOnly(
                      member.id,
                      permission.id,
                      !!checked
                    )
                  }}
                >
                  {permission.name}
                </DropdownMenuCheckboxItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default MemberPermissionsCell
